import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { fetchCommunityStructure } from '../../services/communityService';
import { CommunityForumProvider, useCommunityForum } from './CommunityForumContext';
import CommunitySidebar from './CommunitySidebar';
import CommunityRightPanel from './CommunityRightPanel';
import CommunityTopicListView from './CommunityTopicListView';
import CommunityTopicDetailView from './CommunityTopicDetailView';
import CommunityChatView from './CommunityChatView';
import CommunityAdminPanel from './CommunityAdminPanel';
import CreateTopicModal from './CreateTopicModal';
import { CommunityDevModeProvider, useCommunityDevMode } from './CommunityDevModeContext';
import { CommunityDevBar, CommunityDevAdminGate } from './CommunityDevUi';
import MarketProfileTab from './marketProfile/MarketProfileTab';
import MarketProfileOnboardingModal, {
  isMarketProfileOnboardingNeeded,
  markMarketProfileOnboardingShown,
} from './marketProfile/MarketProfileOnboardingModal';
import {
  loadMarketProfileForUser,
  reconcileMarketProfileWithAuth,
  saveMarketProfile,
  seedMarketProfileIfNeeded,
} from './marketProfile/marketProfileStore';
import { fetchRemoteMarketProfile } from '../../services/adminCommunityService';
import type { MarketProfileNavTarget } from './marketProfile/marketProfileCta';
import type { CommunityChat, CommunityForum, CommunitySidebarHeader, NavSelection } from './types';
import './CommunityForum.css';

type View = 'content' | 'topic' | 'admin' | 'profile';

function firstNavSelection(
  activeHeaders: CommunitySidebarHeader[],
  getForumsByHeader: (id: string) => CommunityForum[],
  getChatsByHeader: (id: string) => CommunityChat[],
): NavSelection | null {
  for (const header of activeHeaders) {
    const chats = getChatsByHeader(header.id);
    if (chats.length > 0) return { kind: 'chat', id: chats[0].id };
    const forums = getForumsByHeader(header.id);
    if (forums.length > 0) return { kind: 'forum', id: forums[0].id };
  }
  return null;
}

function CommunityForumInner({ realIsAdmin }: { realIsAdmin: boolean }) {
  const { user } = useAuth();
  const { marketProfileRevision } = useCommunityDevMode();
  const {
    state,
    activeForums,
    activeHeaders,
    isAdmin,
    getForumsByHeader,
    getChatsByHeader,
  } = useCommunityForum();
  const [searchParams, setSearchParams] = useSearchParams();

  const [view, setView] = useState<View>('content');
  const [selection, setSelection] = useState<NavSelection | null>(null);
  const [activeTopicId, setActiveTopicId] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [profileTick, setProfileTick] = useState(0);
  const [profileEntry, setProfileEntry] = useState<MarketProfileNavTarget>('overview');
  const [onboardingOpen, setOnboardingOpen] = useState(false);

  useEffect(() => {
    if (!user?.email) return;
    let cancelled = false;
    const seeded = seedMarketProfileIfNeeded(user.email);
    if (seeded) setProfileTick((t) => t + 1);

    void (async () => {
      try {
        const remote = await fetchRemoteMarketProfile();
        if (cancelled) return;
        if (remote) {
          const next = reconcileMarketProfileWithAuth(
            seeded ?? loadMarketProfileForUser(user.email, user.id),
            user.id,
            remote,
            user.email,
          );
          saveMarketProfile(next, user.email, user.id);
          setProfileTick((t) => t + 1);
        }
      } catch {
        /* API недоступен — оставляем сессию вкладки */
      }
      if (cancelled) return;
      if (isMarketProfileOnboardingNeeded(user.email)) {
        markMarketProfileOnboardingShown();
        setOnboardingOpen(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.email, user?.id]);

  useEffect(() => {
    if (marketProfileRevision > 0) {
      setProfileTick((t) => t + 1);
    }
  }, [marketProfileRevision]);

  const closeOnboarding = useCallback(() => {
    setOnboardingOpen(false);
  }, []);

  const openProfileFromOnboarding = useCallback((target: MarketProfileNavTarget) => {
    setProfileEntry(target);
    setView('profile');
    setOnboardingOpen(false);
  }, []);

  const onboardingModal = onboardingOpen ? (
    <MarketProfileOnboardingModal
      userEmail={user?.email}
      onClose={closeOnboarding}
      onOpenShare={() => openProfileFromOnboarding('share')}
    />
  ) : null;

  useEffect(() => {
    if (view === 'admin' && !isAdmin) setView('content');
  }, [view, isAdmin]);

  const defaultSelection = useMemo(
    () => firstNavSelection(activeHeaders, getForumsByHeader, getChatsByHeader),
    [activeHeaders, getForumsByHeader, getChatsByHeader],
  );

  useEffect(() => {
    if (view === 'admin' || view === 'topic' || view === 'profile') return;
    if (!selection) {
      if (defaultSelection) setSelection(defaultSelection);
      return;
    }
    const valid =
      selection.kind === 'forum'
        ? activeForums.some((f) => f.id === selection.id)
        : activeHeaders.some((h) => getChatsByHeader(h.id).some((c) => c.id === selection.id));
    if (!valid && defaultSelection) setSelection(defaultSelection);
  }, [selection, defaultSelection, activeForums, activeHeaders, getChatsByHeader, view]);

  useEffect(() => {
    const topicParam = searchParams.get('topic');
    if (!topicParam || !state.topics.some((t) => t.id === topicParam)) return;
    const topic = state.topics.find((t) => t.id === topicParam)!;
    setActiveTopicId(topicParam);
    setSelection({ kind: 'forum', id: topic.sectionId });
    setView('topic');
  }, [searchParams, state.topics]);

  const activeForum = useMemo(() => {
    if (selection?.kind !== 'forum') return null;
    return activeForums.find((f) => f.id === selection.id) ?? null;
  }, [selection, activeForums]);

  const activeChat = useMemo(() => {
    if (selection?.kind !== 'chat') return null;
    for (const header of activeHeaders) {
      const chat = getChatsByHeader(header.id).find((c) => c.id === selection.id);
      if (chat) return chat;
    }
    return null;
  }, [selection, activeHeaders, getChatsByHeader]);

  const activeTopic = useMemo(
    () => (activeTopicId ? state.topics.find((t) => t.id === activeTopicId) ?? null : null),
    [state.topics, activeTopicId],
  );

  const openTopic = useCallback((topicId: string) => {
    setActiveTopicId(topicId);
    setView('topic');
    setSearchParams({ topic: topicId });
  }, [setSearchParams]);

  const backToTopics = useCallback(() => {
    setActiveTopicId(null);
    setView('content');
    setSearchParams({});
  }, [setSearchParams]);

  const selectForum = useCallback((forumId: string) => {
    setSelection({ kind: 'forum', id: forumId });
    setView('content');
    setActiveTopicId(null);
    setSearchParams({});
  }, [setSearchParams]);

  const selectChat = useCallback((chatId: string) => {
    setSelection({ kind: 'chat', id: chatId });
    setView('content');
    setActiveTopicId(null);
    setSearchParams({});
  }, [setSearchParams]);

  const userLabel =
    user?.user_metadata?.full_name
    || user?.user_metadata?.name
    || user?.email?.split('@')[0]
    || 'Участник';

  const hasNavigation = activeHeaders.some(
    (h) => getForumsByHeader(h.id).length > 0 || getChatsByHeader(h.id).length > 0,
  );

  const sidebar = (
    <CommunitySidebar
      selection={view === 'admin' || view === 'profile' ? null : selection}
      onSelectForum={selectForum}
      onSelectChat={selectChat}
      onOpenAdmin={() => setView('admin')}
      onOpenProfile={(target) => {
        setProfileEntry(target);
        setView('profile');
      }}
      profileActive={view === 'profile'}
      profileTick={profileTick}
      userLabel={userLabel}
      realIsAdmin={realIsAdmin}
    />
  );

  if (view === 'profile') {
    return (
      <>
        <div className="dcf-page dcf-page--with-dev">
          {sidebar}
          <main className="dcf-main">
            <CommunityDevBar realIsAdmin={realIsAdmin} />
            <div className="dcf-main__inner dcf-main__inner--profile">
              <MarketProfileTab
                entryScreen={profileEntry}
                onChanged={() => setProfileTick((t) => t + 1)}
              />
            </div>
          </main>
        </div>
        {onboardingModal}
      </>
    );
  }

  if (view === 'admin' && isAdmin) {
    return (
      <>
        <div className="dcf-page dcf-page--with-dev">
          {sidebar}
          <main className="dcf-main">
            <CommunityDevBar realIsAdmin={realIsAdmin} />
            <div className="dcf-main__inner">
              <CommunityAdminPanel onBack={() => setView('content')} realIsAdmin={realIsAdmin} />
            </div>
          </main>
        </div>
        {onboardingModal}
      </>
    );
  }

  return (
    <>
      <div className="dcf-page dcf-page--with-dev">
      {sidebar}

      <main className="dcf-main">
        <CommunityDevBar realIsAdmin={realIsAdmin} />
        <div className="dcf-main__inner">
          {!hasNavigation ? (
            <div className="dcf-empty">
              <p className="dcf-empty__title">Навигация пока не настроена</p>
              <p className="dcf-empty__text">
                Администратор может создать заголовки, форумы и чаты для обсуждений участников рынка.
              </p>
              <CommunityDevAdminGate
                realIsAdmin={realIsAdmin}
                isAdmin={isAdmin}
                label="Настроить навигацию"
                storagePath="UI: admin panel"
              >
                <div className="dcf-empty__action">
                  <button type="button" className="dcf-btn dcf-btn--primary" onClick={() => setView('admin')}>
                    Настроить навигацию
                  </button>
                </div>
              </CommunityDevAdminGate>
            </div>
          ) : view === 'topic' && activeTopic && activeForum ? (
            <CommunityTopicDetailView
              topic={activeTopic}
              sectionName={activeForum.name}
              onBack={backToTopics}
              realIsAdmin={realIsAdmin}
            />
          ) : selection?.kind === 'chat' && activeChat ? (
            <CommunityChatView chat={activeChat} realIsAdmin={realIsAdmin} />
          ) : selection?.kind === 'forum' && activeForum ? (
            <CommunityTopicListView
              section={activeForum}
              onOpenTopic={openTopic}
              onCreateTopic={() => setCreateModalOpen(true)}
              realIsAdmin={realIsAdmin}
            />
          ) : null}
        </div>
      </main>

      {view !== 'admin' && selection?.kind === 'forum' && (
        <CommunityRightPanel onOpenTopic={openTopic} />
      )}

      {createModalOpen && activeForum && (
        <CreateTopicModal
          sectionId={activeForum.id}
          sectionName={activeForum.name}
          onClose={() => setCreateModalOpen(false)}
          onCreated={(topicId) => {
            setCreateModalOpen(false);
            openTopic(topicId);
          }}
        />
      )}

      </div>
      {onboardingModal}
    </>
  );
}

function CommunityForumShell({ realIsAdmin }: { realIsAdmin: boolean }) {
  const { effectiveIsAdmin } = useCommunityDevMode();
  const { user } = useAuth();
  const isAdmin = effectiveIsAdmin(realIsAdmin);

  return (
    <CommunityForumProvider isAdmin={isAdmin} viewerEmail={user?.email ?? null}>
      <CommunityForumInner realIsAdmin={realIsAdmin} />
    </CommunityForumProvider>
  );
}

export default function CommunityForumPage() {
  const { user, loading: authLoading } = useAuth();
  const [isCommunityAdmin, setIsCommunityAdmin] = useState(false);
  const [adminChecked, setAdminChecked] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setIsCommunityAdmin(false);
      setAdminChecked(true);
      return;
    }
    let cancelled = false;
    (async () => {
      const res = await fetchCommunityStructure();
      if (cancelled) return;
      setIsCommunityAdmin(res.ok ? res.isCommunityAdmin : false);
      setAdminChecked(true);
    })();
    return () => { cancelled = true; };
  }, [user, authLoading]);

  if (authLoading || !adminChecked) {
    return (
      <div className="dcf-page dcf-page--loading" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <p className="text-sm text-slate-500">Загрузка сообщества…</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="dcf-auth-gate">
        <h1 className="text-lg font-semibold text-slate-900">Сообщество datacircle</h1>
        <p className="mt-3 text-sm text-slate-600">
          Войдите в аккаунт, чтобы участвовать в обсуждениях и задавать вопросы участникам рынка.
        </p>
        <Link to="/" className="dcf-btn dcf-btn--primary mt-6 inline-flex">
          На главную
        </Link>
      </div>
    );
  }

  return (
    <CommunityDevModeProvider userEmail={user?.email}>
      <CommunityForumShell realIsAdmin={isCommunityAdmin} />
    </CommunityDevModeProvider>
  );
}
